import type {
  DataSourceField,
  FilterGroup,
} from '@risksmart-app/shared/reporting/api/schema';
import {
  datasourceFieldSchema,
  datasourceRequestSchema,
  filterSchema,
} from '@risksmart-app/shared/reporting/api/schema';
import type { useGetCustomDatasourceById } from 'src/hooks/queries/custom-datasource/useGetCustomDatasourceById';
import type { useGetCustomDatasources } from 'src/hooks/queries/custom-datasource/useGetCustomDatasources';
import { z } from 'zod';

import type { SelectedField } from './update/field-selection/fieldSelectionSchema';
import type { RelatedDataSource } from './update/types';

type GetCustomDatasourcesData = NonNullable<
  ReturnType<typeof useGetCustomDatasources>['data']
>;
type GetCustomDatasourceByIdData = NonNullable<
  ReturnType<typeof useGetCustomDatasourceById>['data']
>;

export type CustomDatasourceTableFields = {
  Id: string;
  CreatedByUserName: string | undefined;
  ModifiedByUserName: string | undefined;
  Title: string | undefined;
  ModifiedAtTimestamp: string;
  ModifiedByUser: string;
  CreatedByUser: string;
  CreatedAtTimestamp: string;
};

export type CustomDatasource =
  GetCustomDatasourcesData['custom_datasource'][number];

type CustomDatasourceDetails = NonNullable<
  GetCustomDatasourceByIdData['custom_datasource_by_pk']
>;

type SavedDataSourceField = DataSourceField & Pick<SelectedField, 'label'>;

type CustomDatasourceJsonColumns = {
  Datasources: unknown;
  Fields?: unknown;
  Filters?: unknown;
};

type ParsedCustomDatasource<T extends CustomDatasourceJsonColumns> = Omit<
  T,
  'Datasources' | 'Fields' | 'Filters'
> & {
  Filters?: FilterGroup;
  Datasources: RelatedDataSource[];
  Fields?: SavedDataSourceField[];
};

export type TypedCustomDatasource =
  ParsedCustomDatasource<CustomDatasourceDetails>;

// Persisted values predate request abuse ceilings. Keep unknown saved fields
// for round trips; execution still validates the request on the server.
const savedFilterSchema = filterSchema
  .extend({
    field: datasourceFieldSchema.passthrough(),
    value: z.string().or(z.number()).or(z.null()),
  })
  .passthrough();
// Bound parser recursion before visiting historical, user-controlled JSON.
const buildSavedFilterGroupSchema = (depth: number): z.ZodType<FilterGroup> =>
  z
    .object({
      operation: z.enum(['and', 'or']),
      filters:
        depth <= 1
          ? z.array(savedFilterSchema)
          : z.array(
              savedFilterSchema.or(buildSavedFilterGroupSchema(depth - 1))
            ),
    })
    .passthrough();
const savedFilterGroupSchema = buildSavedFilterGroupSchema(20);

const customDatasourceJsonSchema = z.object({
  Datasources: z.array(datasourceRequestSchema.passthrough()),
  Fields: z
    .array(
      datasourceFieldSchema
        .extend({ label: z.string().nullish() })
        .passthrough()
    )
    .nullish(),
  Filters: savedFilterGroupSchema.nullish(),
});

export const parseTypedCustomDatasource = <
  T extends CustomDatasourceJsonColumns,
>(
  value: T
): ParsedCustomDatasource<T> => {
  const parsed = customDatasourceJsonSchema.parse(value);

  return {
    ...value,
    Datasources: parsed.Datasources.map(
      ({ latest, parentIndex, ...datasource }) => ({
        ...datasource,
        ...(latest === null || latest === undefined ? {} : { latest }),
        ...(parentIndex === null || parentIndex === undefined
          ? {}
          : { parentIndex }),
      })
    ),
    Fields: parsed.Fields ?? undefined,
    Filters: parsed.Filters ?? undefined,
  };
};

export const parseOptionalTypedCustomDatasource = <
  T extends CustomDatasourceJsonColumns,
>(
  value: T | null | undefined
): ParsedCustomDatasource<T> | null | undefined => {
  if (value === null || value === undefined) {
    return value;
  }

  return parseTypedCustomDatasource(value);
};

/** Read only the selected report; an unsupported saved record cannot hide others. */
export const readSelectedCustomDatasource = <
  T extends CustomDatasourceJsonColumns & { Id: string },
>(
  rows: readonly T[],
  id: string | null | undefined
): { data: ParsedCustomDatasource<T> | undefined; invalid: boolean } => {
  const row = rows.find((candidate) => candidate.Id === id);
  if (!row) {
    return { data: undefined, invalid: false };
  }
  if (!customDatasourceJsonSchema.safeParse(row).success) {
    return { data: undefined, invalid: true };
  }

  return { data: parseTypedCustomDatasource(row), invalid: false };
};
